/*  MUI  */
import { Button, FormControl, Grid, Typography } from '@mui/material';

/*  Shared  */
import {
    BusinessOwnerPicker,
    LifecyclePicker,
    RiskPicker
} from 'application/Shared/Components/Pickers';
import { LifecycleStatus, RiskLevel } from 'application/Shared/Constants';
import { Helpers, Notify, Transition } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { TenantApp } from 'application/Shared/types/tenantApp';

/* Hooks */
import { usePostAppDefault } from 'hooks/dataAccess/AppDefault/appDefault';
import { usePutTenantApplicationLink } from 'hooks/dataAccess/Tenant/ApplicationLink/tenantApplicationLink';

/*  NPM  */
import { cloneDeep } from 'lodash';

/*  Objects  */
import OwnerFn from 'objects/functions/BusinessOwner';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Modal, ModalBody, ModalFooter } from '../Components';

interface BusinessOwnerObject {
    businessOwnerPlatformUserId: string | null;
    managed3rdParty: boolean;
    managedByPartner: boolean;
    noBusinessOwnerRequired: boolean;
}

const RiskSelection = ({
    risk,
    setRisk
}: {
    risk: RiskLevel | null;
    setRisk: React.Dispatch<React.SetStateAction<RiskLevel | null>>;
}) => {
    const { t } = useTranslation();
    return (
        <Grid item xs={12}>
            <Typography color="textSecondary" fontWeight={600}>
                {t('Common.risk')}
            </Typography>
            <RiskPicker
                noFullWidth
                unselectedOption="No Risk Assigned"
                unselectedValue={null}
                value={risk}
                onChange={(v: RiskLevel) => setRisk(v)}
            />
        </Grid>
    );
};

const OwnerSelection = ({
    appDefaults,
    businessOwner,
    handleBusinessOwnerChange,
    saving
}: {
    appDefaults?: boolean;
    businessOwner: BusinessOwnerObject;
    handleBusinessOwnerChange: (e: string) => void;
    saving: boolean;
}) => {
    const { t } = useTranslation();

    const unselectedOption = appDefaults
        ? 'No Default'
        : t('ManageApplicationAdmin.no_user_selected');

    return (
        <FormControl fullWidth>
            <Typography color="textSecondary" fontWeight={600}>
                {t('Common.business_owner')}
            </Typography>
            <BusinessOwnerPicker
                multiple={false}
                disabled={saving}
                disableAllOption
                unselectedOption={unselectedOption}
                unselectedValue="notAssigned"
                value={businessOwner.businessOwnerPlatformUserId}
                noBusinessOwnerRequired={businessOwner.noBusinessOwnerRequired}
                managedByPartner={businessOwner.managedByPartner}
                managed3rdParty={businessOwner.managed3rdParty}
                hideUsersInOrganization={appDefaults}
                onChange={(e: string) => handleBusinessOwnerChange(e)}
            />
        </FormControl>
    );
};

const Selections = ({
    appDefaults,
    businessOwner,
    handleBusinessOwnerChange,
    lifecycleStage,
    risk,
    saving,
    setLifecycleStage,
    setRisk
}: {
    appDefaults?: boolean;
    businessOwner: BusinessOwnerObject;
    handleBusinessOwnerChange: (e: string) => void;
    lifecycleStage: LifecycleStatus | null;
    risk: RiskLevel | null;
    saving: boolean;
    setLifecycleStage: React.Dispatch<React.SetStateAction<LifecycleStatus>>;
    setRisk: React.Dispatch<React.SetStateAction<RiskLevel | null>>;
}) => {
    const { t } = useTranslation();
    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography>{t('Applications.classify.meaning')}</Typography>
            </Grid>
            {appDefaults && <RiskSelection risk={risk} setRisk={setRisk} />}
            <Grid item xs={12}>
                <FormControl fullWidth>
                    <Typography color="textSecondary" fontWeight={600}>
                        {t('Common.lifecycleStage')}
                    </Typography>
                    <LifecyclePicker
                        disabled={saving}
                        multiple={false}
                        disableAllOption
                        value={lifecycleStage}
                        onChange={(e: LifecycleStatus) => setLifecycleStage(e)}
                    />
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <OwnerSelection
                    appDefaults={appDefaults}
                    businessOwner={businessOwner}
                    handleBusinessOwnerChange={handleBusinessOwnerChange}
                    saving={saving}
                />
            </Grid>
        </Grid>
    );
};

/**
 * Modal component for bulk classifying applications
 * @param {Object} props - Component props
 * @param {boolean} [props.appDefaults] - Flag indicating if this is for app defaults
 * @param {Array} props.applications - Array of applications to classify
 * @param {Function} props.closeModal - Function to close the modal
 * @param {string | null} [props.defaultCategoryId] - Default category ID to use if none set
 * @param {Object} [props.rowMap] - Map of application rows
 * @param {Function} [props.onSave] - Callback function called after successful save
 * @param {boolean} props.open - Flag indicating if we are to open the modal
 */
export const BulkClassifyModal = ({
    appDefaults,
    applications,
    closeModal,
    defaultCategoryId,
    rowMap = {},
    onSave,
    open
}: {
    appDefaults?: boolean;
    applications: TenantApp[] | string[];
    closeModal: (value: boolean) => void;
    defaultCategoryId?: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rowMap?: Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSave?: (value: boolean | { rowMap: Record<string, any> }) => void;
    open: boolean;
}) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const localRowMap = cloneDeep(rowMap);

    const { putTenantApplicationLink } = usePutTenantApplicationLink();
    const { postAppDefault } = usePostAppDefault();

    // If we are classifying a single app, we can show the current selections.
    const isItASingleApp = applications?.length === 1;

    const initialRisk = appDefaults ? RiskLevel.Medium : null;

    const [businessOwner, setBusinessOwner] = useState({
        businessOwnerPlatformUserId: isItASingleApp
            ? (applications[0] as TenantApp).businessOwnerPlatformUserId
            : null,
        managed3rdParty: isItASingleApp
            ? (applications[0] as TenantApp).managedByPartner
            : false,
        managedByPartner: isItASingleApp
            ? (applications[0] as TenantApp).managedByPartner
            : false,
        noBusinessOwnerRequired: isItASingleApp
            ? (applications[0] as TenantApp).noBusinessOwnerRequired
            : false
    });
    const [lifecycleStage, setLifecycleStage] = useState(
        isItASingleApp
            ? ((applications[0] as TenantApp).status as LifecycleStatus)
            : LifecycleStatus.NotConfigured
    );
    const [risk, setRisk] = useState(
        isItASingleApp ? (applications[0] as TenantApp).risk : initialRisk
    );
    const [saving, setSaving] = useState(false);

    const handleBusinessOwnerChange = (e: string) => {
        const {
            businessOwnerPlatformUserId,
            managed3rdParty,
            managedByPartner,
            noBusinessOwnerRequired
        } = OwnerFn.handleValues(e);
        setBusinessOwner({
            businessOwnerPlatformUserId,
            managed3rdParty,
            managedByPartner,
            noBusinessOwnerRequired
        });
    };

    const save = async () => {
        Notify.i(t('Applications.updatingWait'));
        if (appDefaults) {
            await saveDefaults();
        } else {
            await saveClientApp();
            if (onSave) onSave(true);
        }
    };

    const saveClientApp = async () => {
        try {
            setSaving(true);
            await Promise.all(
                (applications as TenantApp[]).map(async (application) => {
                    const applicationId = application.id;
                    const params: {
                        businessOwnerPlatformUserId: string | null;
                        managed3rdParty: boolean;
                        managedByPartner: boolean;
                        noBusinessOwnerRequired: boolean;
                        categoryId: string | null | undefined;
                        platformTenantId: string | null;
                        status: LifecycleStatus;
                    } = {
                        ...businessOwner,
                        // Do I really need to send the category?
                        categoryId: application.categoryId || defaultCategoryId, // set category to default "Business Operations / Other" if no category is set
                        platformTenantId: null,
                        // TODO: Remove after Bob's MR changes
                        status: lifecycleStage
                    };
                    if (clientId) {
                        params.platformTenantId = clientId;
                    }

                    await putTenantApplicationLink({
                        id: applicationId,
                        params
                    });
                })
            );
            Helpers.clearToasts();
            Notify.s(t('ManageApplicationAdmin.default_settings_saved'));
        } catch (e) {
            debug.e(e);
            Notify.e(t('ManageApplicationAdmin.default_settings_error'));
        }
        setSaving(false);
    };

    const saveDefaults = async () => {
        let businessOwnerConfiguration;

        if (businessOwner.noBusinessOwnerRequired) {
            businessOwnerConfiguration = 'noBusinessOwnerRequired';
        } else if (businessOwner.managedByPartner) {
            businessOwnerConfiguration = 'managedByPartner';
        } else if (businessOwner.managed3rdParty) {
            businessOwnerConfiguration = 'managed3rdParty';
        } else {
            businessOwnerConfiguration = '';
        }

        try {
            setSaving(true);
            await Promise.all(
                (applications as string[]).map(
                    async (applicationId: string) => {
                        if (!applicationId) return;

                        const params: {
                            applicationId: string;
                            businessOwnerConfiguration: string;
                            risk: RiskLevel | null;
                            status: LifecycleStatus;
                        } = {
                            applicationId,
                            businessOwnerConfiguration,
                            risk,
                            status: lifecycleStage
                        };
                        debug.dev('App Default POST Payload:', params);
                        await postAppDefault(params);
                        localRowMap[applicationId] = {
                            ...localRowMap[applicationId],
                            businessOwnerConfiguration,
                            defaultSet: true,
                            risk,
                            status: lifecycleStage
                        };
                    }
                )
            );
            Helpers.clearToasts();
            Notify.s(t('ManageApplicationAdmin.default_settings_saved'));
            if (onSave) {
                onSave({ rowMap: localRowMap });
            }
        } catch (e) {
            debug.e(e);
            Notify.e(t('ManageApplicationAdmin.default_settings_error'));
        }
        setSaving(false);
    };

    const appCount = Helpers.count(applications);

    return (
        <Modal
            open={open}
            fullWidth
            scroll="paper"
            TransitionComponent={Transition}
            onClose={() => {
                closeModal(false);
            }}
            title={t('Common.bulkEditWithCount', {
                count: appCount,
                name: (applications[0] as TenantApp)?.application?.name
            })}
        >
            <ModalBody dividers>
                <Selections
                    appDefaults={appDefaults}
                    businessOwner={businessOwner}
                    handleBusinessOwnerChange={handleBusinessOwnerChange}
                    lifecycleStage={lifecycleStage}
                    risk={risk}
                    saving={saving}
                    setLifecycleStage={setLifecycleStage}
                    setRisk={setRisk}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    data-trackid="bulkEditAppModal_closeBtn"
                    variant="outlined"
                    disabled={saving}
                    color="primary"
                    onClick={() => closeModal(false)}
                >
                    {t('Common.close')}
                </Button>
                <Button
                    data-trackid="bulkEditAppModal_saveBtn"
                    variant="contained"
                    disabled={saving}
                    color="primary"
                    onClick={() => save()}
                >
                    {t('Common.classify')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};
